import {VNode} from 'snabbdom';

import {Section, SelectAllMechanism} from 'common/components/items-menu/types';
import {ButtonSize} from 'common/components/button/constants';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {Segmentation} from 'common/types/reports/query';
import {PropertyType} from 'common/report/constants';
import {Directions} from 'common/components/drop-menu/types';

import {AdvancedMenuValue} from '../query-entry-section/types';
import * as PropertiesMenu from './properties-menu/types';
import type {ChildMenu} from './child-menu';
import {MetricType, QueryBlockTheme, SelectorType} from './constants';

export enum MenuType {
  Breakdown = `breakdown`,
  Cohorts = `cohorts`,
  Events = `events`,
  Filter = `filter`,
  Properties = `properties`,
  Tabbed = `tabbed`,
  Time = `time`,
  MultiEvents = `multi-events`,
  Count = `count`,
}

export type IncludeCustomPropertySetting = `none` | `persisted-only` | `persisted-non-behavioral-only` | `all`;

export type QueryBlockState = {
  childMenu: ChildMenu<any>;
  isHeaderMenuOpen: boolean;
  isMenuOpen: boolean;
  openedMenuForModal: boolean;
  isModalOpen: boolean;
  searchFilter: string;

  // TODO: Add types for these
  eventData: any;
  selected: any;
  staged: any; // TODO: being used in breakdown-menu, this should be removed
  tooltipData: any;
  keydownProxyEl: any;
  getSortedMenuSections: PropertiesMenu.GetSortedMenuSections;
};

export type QueryBlockAttrs = {
  type: MenuType;
  'all-data-groups': boolean;
  'allow-behavioral-property-creation': boolean;
  'allow-typecast-pill-action'?: PropertiesMenu.TypecastPillAction;
  'apply-button-text': string;
  'highlight-selections': boolean;
  'metric-type': MetricType;
  'min-selection': number;
  autofocus: boolean;
  'data-group-id': string;
  'data-groups': DataGroup[];
  'date-range': any; // {type: `json`, description: `Only for time-comparison menu type`},
  disabled: boolean;
  'disabled-items': `json`;
  'display-context'?: string;
  'enable-enhanced-list-filters': boolean;
  'enable-workspace-filter-restrictions': boolean;
  'disable-value-selection': boolean;
  'exclude-autotrack-events': boolean;
  'exclude-custom-events': boolean;
  'include-custom-properties': IncludeCustomPropertySetting;
  'exclude-dimension-properties': boolean;
  'exclude-lookup-table-properties': boolean;
  'excluded-elements': any; // {type: `json`, default: []},
  'event-names': any; // {type: `json`, default: null},
  'header-segments': any; // `json`,
  'header-text': string;
  'help-text': string;
  'input-only': boolean;
  tooltip: any; // {type: `json`,}
  'is-blocked-people': boolean;
  label: string;
  'max-data-history-days': number;
  'cohort-multiselect': boolean;
  multiple: boolean;
  open: boolean;
  'prefix-label': string;
  'suffix-label': string;
  'property-combine-mode': string; // {type: `string`, default: null},
  'hide-events': boolean;
  'hide-all-properties-select': boolean;
  'project-has-people': boolean;
  'project-has-cohorts': boolean;
  'project-id': number;
  'project-is-free-plan': boolean;
  readonly: boolean;
  removable: boolean;
  'dropdown-alignment': string;
  selected: PropertiesMenu.Selection; // {type: `json`, default: null},
  inverted: boolean;
  'selector-icon': string;
  'selector-right-icon': string;
  'selector-type': SelectorType;
  'show-any-event': boolean;
  'show-session-event': boolean;
  'show-all-events': boolean;
  'show-all-tab': boolean;
  'show-all-users-cohort': boolean;
  'show-top-events': boolean;
  'single-data-group': string;
  'support-b2b-cohorts': boolean;
  'inline-cohorts-override': any; // `json`,
  'is-multi-attribution': boolean;
  'hide-edit-controls': boolean;
  'show-special-date-property-unit': boolean;
  'show-special-dataset-property': boolean;
  theme: QueryBlockTheme;
  'button-size': ButtonSize;
  tabs: any; //`json`,
  'track-name': string;
  'other-group-by-options': string;
  'other-filter-options': string;
  'user-id': number;
  'workspace-id': number;
  'project-as-of-date': string;
  'search-placeholder': string;
  'show-select-all': boolean;
  'select-all-mechanism'?: SelectAllMechanism;
  'select-all-disabled': boolean;
  'hide-recently-viewed': boolean;
  'hide-section-labels': boolean;
  'selectable-sensitive-properties': boolean;
  'menu-item-types': any; // json,
  'menu-alignment': Directions;
  'menu-offset': number;
  'type-filter': PropertyType[];

  // advanced sections (currently only consumed in count-menu)
  'advanced-menu-sections'?: Array<Section<AdvancedMenuValue>>;
};

export type QueryBlockParams = {
  labelRenderer?: () => VNode;
  listProperty?: Segmentation;
};

export type QueryBlockJsxProps = {
  attrs?: Partial<QueryBlockAttrs>;
  props?: Obj;
  on?: Obj;
};
